import * as Schema from "effect/Schema";

import { HostStoreTargetRefusalWire } from "./hostStoreLockout.ts";
import {
  EnvironmentId,
  IsoDateTime,
  PortSchema,
  PositiveInt,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const LocalLifecycleCommand = Schema.Literals(["start", "run", "status", "stop"]);
export type LocalLifecycleCommand = typeof LocalLifecycleCommand.Type;

export const LocalLifecycleOutcome = Schema.Literals([
  "starting",
  "ready",
  "stopped",
  "blocked",
  "stale",
  "contradictory",
  "failed",
]);
export type LocalLifecycleOutcome = typeof LocalLifecycleOutcome.Type;

export const LocalLifecycleResultCode = Schema.Literals([
  "starting",
  "ready",
  "stopped",
  "already-running",
  "blocked",
  "stale",
  "contradictory",
  "unsupported-target-platform",
  "invalid-application-installation",
  "runtime-dependency-unavailable",
  "host-store-recovery-required",
  "host-store-access-error",
  "unsupported-host-store-version",
  "runtime-identity-conflict",
  "listener-unavailable",
  "spawn-failed",
  "exited-before-readiness",
  "readiness-timeout",
  "shutdown-timeout",
  "system-error",
]);
export type LocalLifecycleResultCode = typeof LocalLifecycleResultCode.Type;

export const LocalExecutionImageIdentity = Schema.Struct({
  path: TrimmedNonEmptyString,
  device: TrimmedNonEmptyString,
  inode: TrimmedNonEmptyString,
});
export type LocalExecutionImageIdentity = typeof LocalExecutionImageIdentity.Type;

export const LocalRuntimeEvidence = Schema.Struct({
  pid: PositiveInt,
  processStartFingerprint: TrimmedNonEmptyString,
  executionImage: LocalExecutionImageIdentity,
  installedRelease: TrimmedNonEmptyString,
  executingRelease: TrimmedNonEmptyString,
  environmentId: EnvironmentId,
  origin: TrimmedNonEmptyString,
  listener: Schema.Struct({
    host: TrimmedNonEmptyString,
    port: PortSchema,
  }),
  lifecycleGeneration: TrimmedNonEmptyString,
  lifecycleState: Schema.Literals(["starting", "ready", "blocked"]),
  blockedCode: Schema.optionalKey(
    Schema.Literals([
      "host-store-recovery-required",
      "host-store-access-error",
      "unsupported-host-store-version",
      "invalid-application-installation",
    ]),
  ),
  startedAt: IsoDateTime,
});
export type LocalRuntimeEvidence = typeof LocalRuntimeEvidence.Type;

export const LocalServiceSupervision = Schema.Literals(["absent", "enabled", "disabled"]);

export const LocalLifecycleResult = Schema.Struct({
  command: LocalLifecycleCommand,
  outcome: LocalLifecycleOutcome,
  code: LocalLifecycleResultCode,
  storePath: TrimmedNonEmptyString,
  installedRelease: TrimmedNonEmptyString,
  executingRelease: Schema.optionalKey(TrimmedNonEmptyString),
  evidence: Schema.optionalKey(LocalRuntimeEvidence),
  supervision: Schema.optionalKey(LocalServiceSupervision),
  failedOperation: Schema.optionalKey(TrimmedNonEmptyString),
  /**
   * The path a refusal named, when it named one that is not the store root.
   * A Host Store Access Error is usually about one target inside the store —
   * a subdirectory, the marker — and the root alone does not say which, so a
   * user cannot act on it (#957).
   */
  failedPath: Schema.optionalKey(TrimmedNonEmptyString),
  /**
   * Which property of a Host Store target refused, when one did. Distinct from
   * `failedOperation`, which says what the service was doing: the operation is
   * the service's own phrase and the refusal is the closed vocabulary a surface
   * turns into its own words.
   */
  refusal: Schema.optionalKey(HostStoreTargetRefusalWire),
  retryable: Schema.Boolean,
  message: Schema.optionalKey(TrimmedNonEmptyString),
});
export type LocalLifecycleResult = typeof LocalLifecycleResult.Type;

/**
 * Whether a lifecycle result has decided anything yet.
 *
 * A retryable failure means the answer is not known — a live Environment
 * Service that was too busy to answer one probe reads exactly like this — so it
 * is not a verdict and must not be presented or acted on as one. Every surface
 * that has to tell "not proven yet" from "proven impossible" asks here, so the
 * rule is stated once rather than re-derived per entry point.
 */
export const localLifecycleIsUndecided = (result: LocalLifecycleResult): boolean =>
  result.outcome === "failed" && result.retryable;

/**
 * Whether a lifecycle result describes a service that is not serving its AFAR
 * Host Store, and so may be started by whoever is asking.
 *
 * `starting` is one of these, which is the part worth stating. It means either
 * that startup is wanted — the Environment Service is a detached child that is
 * never restarted for us, so that records an intent nobody is acting on — or
 * that another process is activating an installed release. Neither is a service
 * answering for this store, and reading either as somebody else's business in
 * progress is what left the Environment Service CLI reporting a start it never
 * made, indefinitely (#941). What keeps a genuinely concurrent start from
 * becoming a second service is the launch lock, not this predicate.
 *
 * Every entry point deciding whether to start asks here, so no surface can
 * re-derive the list and leave one state a dead end.
 */
export const localLifecycleIsNotRunning = (result: LocalLifecycleResult): boolean =>
  result.outcome === "stopped" || result.outcome === "stale" || result.outcome === "starting";
